// File-kind detection for the content/resources viewer + thumbnails.
//
// Content assets store `file_url` as a bucket-relative storage path
// ("content/<Date.now()>-<safeName>") — there's no MIME column in the DB, so
// we infer how to render a file from its extension. Pure + dependency-free so
// it can be unit-tested (scripts/check-file-type.mjs) and shared by the portal
// and admin surfaces.

export type FileKind = "pdf" | "image" | "video" | "audio" | "office" | "other";

const EXT_KIND: Record<string, FileKind> = {
  pdf: "pdf",
  // images
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  avif: "image",
  svg: "image",
  bmp: "image",
  // video
  mp4: "video",
  webm: "video",
  mov: "video",
  m4v: "video",
  ogv: "video",
  // audio
  mp3: "audio",
  wav: "audio",
  m4a: "audio",
  aac: "audio",
  ogg: "audio",
  flac: "audio",
  // office (no native browser preview → download CTA)
  doc: "office",
  docx: "office",
  ppt: "office",
  pptx: "office",
  xls: "office",
  xlsx: "office",
};

const BUCKET_PREFIX = "content/";

/** Extension (lowercase, no dot) of a storage path, or "" if none. */
function extOf(fileUrl: string): string {
  const name = fileUrl.split("/").pop() ?? fileUrl;
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

export function inferFileKind(fileUrl: string | null | undefined): FileKind {
  if (!fileUrl) return "other";
  return EXT_KIND[extOf(fileUrl)] ?? "other";
}

/** Kinds the browser can render inline (PDF/image/video/audio). */
export function isInlineViewable(kind: FileKind): boolean {
  return kind === "pdf" || kind === "image" || kind === "video" || kind === "audio";
}

/**
 * Human-friendly filename for download / display. Strips the `content/`
 * bucket prefix and the `<timestamp>-` mangling prefix that uploadContent /
 * createSignedUploadUrl prepend, e.g.
 *   "content/1738000000000-Q3-Deck.pdf" -> "Q3-Deck.pdf"
 */
export function displayFileName(fileUrl: string | null | undefined): string {
  if (!fileUrl) return "file";
  let name = fileUrl.startsWith(BUCKET_PREFIX)
    ? fileUrl.slice(BUCKET_PREFIX.length)
    : fileUrl;
  name = name.split("/").pop() ?? name;
  // Strip a leading epoch-millis prefix ("1738000000000-").
  name = name.replace(/^\d{10,}-/, "");
  return name || "file";
}
